import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faFilter, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import MultiSelect from '../../components/MultiSelect';
import { fetchColaboradores } from '../../services/colaboradorService';
import { extensaoService } from '../../services/extensaoService';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';

// Opções para dropdowns (mesmo que no formulário de cadastro)
const CARGO_OPTIONS = [
  'Pastor(a)',
  'Missionário(a)',
  'Evangelista',
  'Presbítero',
  'Diácono(nisa)',
  'Aux. de Trabalho',
  'Membro',
  'Líder de Dpto',
  'Congregado'
];

const FUNCAO_OPTIONS = [
  'Coordenador(a)',
  'Professor(a)',
  'Secretário(a)'
];

const STATUS_OPTIONS = [
  'Ativo',
  'Inativo'
];

const columns = [
  { Header: 'ID', accessor: 'idPessoa' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'CPF', accessor: 'cpf' },
  { Header: 'Email', accessor: 'email' },
  { Header: 'Data de Nascimento', accessor: 'dataNascimento', Cell: value => formatDate(value) },
  { Header: 'Telefone Celular', accessor: 'telefoneCelular' },
  { Header: 'Cargo', accessor: 'cargo' },
  { Header: 'Função', accessor: 'funcao' }, // corrigido para usar funcao
  { Header: 'Status', accessor: 'status' }, // adicionado campo status separado
  { Header: 'Extensão', accessor: 'extensaoNome' }, // nome da extensão retornado pelo JOIN
  { Header: 'Cidade', accessor: 'cidade' },
  { Header: 'Estado', accessor: 'estado' },
  { Header: 'País', accessor: 'pais' },
  { Header: 'Estrangeiro', accessor: 'estrangeira', Cell: value => {
    // Se valor é null ou undefined, considera como estrangeiro (mostra "Não")
    if (value === null || value === undefined) {
      return 'Não';
    }
    
    // Tratar Buffer do MySQL
    let booleanValue = false;
    if (value && typeof value === 'object' && value.type === 'Buffer' && value.data) {
      booleanValue = value.data[0] === 1;
    } else {
      // Fallback para outros tipos
      booleanValue = value === 1 || value === true || value === '1';
    }
    
    // Se estrangeira é true, mostra "Sim" (é estrangeiro)
    // Se estrangeira é false, mostra "Não" (não é estrangeiro, é brasileiro)
    return booleanValue ? 'Sim' : 'Não';
  }},
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Alterado Por', accessor: 'alteradoPor' },
  { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => formatDateTime(value) },
];

const pageSizes = [5, 10, 20];

const ColaboradoresList = () => {
  const { user } = useAuth();
  
  const [colaboradores, setColaboradores] = useState([]);
  const [loading, setLoading] = useState(false);
  // Timer do debounce dos filtros de texto
  const timerBuscaRef = useRef(null);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ 
    nome: '', 
    cpf: '', 
    email: '', 
    cargo: '', 
    funcao: '', 
    status: '', 
    cidade: '', 
    estado: '',
    extensoes: [] // Array para múltiplas extensões
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[1]); // padrão 10
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filtrar colunas baseado no nível de acesso
  const colunasAuditoria = ['cadastradoPor', 'alteradoPor', 'alteradoEm'];
  
  const nivelAcesso = user?.nivelAcesso || user?.perfil?.nivelAcesso || user?.nivel_acesso || 0;
  
  const colunasDisponiveis = nivelAcesso >= 5 
    ? columns 
    : columns.filter(col => !colunasAuditoria.includes(col.accessor));
  
  const [visibleColumns, setVisibleColumns] = useState(colunasDisponiveis.map(col => col.accessor));
  const [extensoes, setExtensoes] = useState([]);

  // Carrega as extensões disponíveis para o filtro
  const loadExtensoes = async () => {
    try {
      const response = await extensaoService.list({ limit: 1000 });
      if (response.success && response.data) {
        const extensaoOptions = response.data.map(ext => ({
          id: ext.idExtensao,
          nome: ext.nome
        }));
        setExtensoes(extensaoOptions);
      }
    } catch (error) {
      console.error('Erro ao carregar extensões:', error);
    }
  };

  const loadColaboradores = async (pageNum = page, limit = pageSize, filterParams = filters) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchColaboradores({
        page: pageNum,
        limit,
        ...filterParams
      });

      if (response.success) {
        setColaboradores(response.data);
        setTotalPages(response.pagination.totalPages);
        setTotal(response.pagination.total);
      } else {
        setError('Erro ao carregar colaboradores');
      }
    } catch (err) {
      console.error('Erro ao carregar colaboradores:', err);
      setError('Erro ao carregar colaboradores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExtensoes();
    loadColaboradores();
    return () => clearTimeout(timerBuscaRef.current);
  }, []);

    // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Espera o usuário parar de digitar antes de consultar a API: antes cada
  // tecla disparava uma requisição e a resposta de uma busca antiga podia
  // chegar depois e sobrescrever a lista correta.
  const handleFilterChange = (e) => {
    const newFilters = { ...filters, [e.target.name]: e.target.value };
    setFilters(newFilters);
    setPage(1);

    clearTimeout(timerBuscaRef.current);
    timerBuscaRef.current = setTimeout(() => {
      loadColaboradores(1, pageSize, newFilters);
    }, 400);
  };

  const handleExtensaoFilterChange = (selectedExtensoes) => {
    const newFilters = { ...filters, extensoes: selectedExtensoes };
    setFilters(newFilters);
    setPage(1);
    loadColaboradores(1, pageSize, newFilters);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      loadColaboradores(newPage, pageSize, filters);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1);
    loadColaboradores(1, newPageSize, filters);
  };

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Listagem de Colaboradores</h2>
            <Link
              to="/colaboradores/create"
              className="w-full sm:w-auto px-3 py-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white text-sm sm:text-base font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Novo Colaborador</span>
            </Link>
          </div>

          <ColumnVisibilityModal
            columns={colunasDisponiveis}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />

          {/* Botão para mostrar/ocultar filtros (mobile) */}
          <div className="mb-4">
            <button
              type="button"
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>{showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}</span>
              <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} className="text-xs" />
            </button>
          </div>

          {/* Filtros colapsáveis */}
          {showFilters && (
            <div className="max-w-full mb-6">
              <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
                <input
                  type="text"
                  name="nome"
                  value={filters.nome}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por nome"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <input
                  type="text"
                  name="cpf"
                  value={filters.cpf}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por CPF"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <input
                  type="text"
                  name="email"
                  value={filters.email}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por Email"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <select
                  name="cargo"
                  value={filters.cargo}
                  onChange={handleFilterChange}
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Filtrar por cargo</option>
                  {CARGO_OPTIONS.map(cargo => (
                    <option key={cargo} value={cargo}>{cargo}</option>
                  ))}
                </select>
                <select
                  name="funcao"
                  value={filters.funcao}
                  onChange={handleFilterChange}
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Filtrar por função</option>
                  {FUNCAO_OPTIONS.map(funcao => (
                    <option key={funcao} value={funcao}>{funcao}</option>
                  ))}
                </select>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                >
                  <option value="">Filtrar por status</option>
                  {STATUS_OPTIONS.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <MultiSelect
                  options={extensoes.map(ext => ({ value: ext.id, label: ext.nome }))}
                  value={filters.extensoes}
                  onChange={handleExtensaoFilterChange}
                  placeholder="Filtrar por extensão"
                />
                <input
                  type="text"
                  name="cidade"
                  value={filters.cidade}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por cidade"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
                <input
                  type="text"
                  name="estado"
                  value={filters.estado}
                  onChange={handleFilterChange}
                  placeholder="Filtrar por estado"
                  className="w-full sm:w-auto px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination 
                currentPage={page} 
                totalPages={totalPages}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange} 
                disabled={loading}
              />
            </div>
            <div className="order-1 sm:order-2 flex flex-col sm:flex-row items-start sm:items-center w-full sm:w-auto gap-2">
              <span className="text-sm text-gray-700 whitespace-nowrap">
                {loading ? 'Carregando...' : `Total: ${total} registros`}
              </span>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap">Por página:</label>
                <select
                  id="pageSize"
                  value={pageSize}
                  onChange={e => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1.5 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
                  disabled={loading}
                >
                  {pageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="w-full sm:w-auto flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-50"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>

          {/* Tabela com scroll horizontal em mobile */}
          <div className="max-w-full overflow-x-auto">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="text-gray-600">Carregando colaboradores...</div>
              </div>
            ) : (
              <Table
                columns={colunasDisponiveis.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                  if (col.accessor === 'nome') {
                    return {
                      ...col,
                      Cell: (value, row) => (
                        <div className="flex items-center gap-2">
                          <span>{value}</span>
                          <Link
                            to={`/colaboradores/${row.idPessoa}/edit`}
                            className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                            title="Editar"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        </div>
                      )
                    };
                  }
                  return col;
                })}
                data={colaboradores}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ColaboradoresList;
